import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DefaultButton, Modal, PrimaryButton, Stack, TextField } from "@fluentui/react";
import { localize } from "src/l10n";
import { Label } from "src/ui";
import { FormControl } from "src/ui/components/Forms";
import ConfirmDialog from "src/ui/components/Forms/ConfirmDialog";
import { IApplicationState } from "src/spintr/reducer";
import ErrorMessagebar from "src/ui/components/Messagebars/ErrorMessagebar";
import SpintrLoader from "src/ui/components/Loader";
import PopupHeader from "src/ui/components/PopupHeader";
import { ExternalFile } from "src/chat/components/ExternalFile";
import { capitalizeFirstLetter } from "src/utils";
import { submitShareAsync } from "../../api/shareApi";
import { IShareModalProps, IShareSubmitPayload, IShareTarget } from "./ShareModal.types";
import { shareTargetConfig } from "./shareTargetConfig";
import ShareTargetDropdown from "./ShareTargetDropdown";
import "./ShareModal.scss";

const ShareModal = (props: IShareModalProps) => {
    const currentUser = useSelector((state: IApplicationState) => state.profile.active);

    const [selectedTarget, setSelectedTarget] = useState<IShareTarget>({
        type: "feed",
        id: currentUser ? currentUser.id : 0,
        name: capitalizeFirstLetter(localize("SOCIALA_FLODET")),
    });
    const [message, setMessage] = useState("");
    const [isSharing, setIsSharing] = useState(false);
    const [saveError, setSaveError] = useState<string[]>([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const config = shareTargetConfig[selectedTarget.type];

    // External-file shares only allow a message when sharing to the own feed.
    const showTextarea = config.showTextarea && (!props.isExternalFile || selectedTarget.type === "feed");

    const onShare = () => {
        setIsSharing(true);
        setSaveError([]);

        const payload: IShareSubmitPayload = {
            targetType: selectedTarget.type,
            targetId: selectedTarget.id,
            text: showTextarea ? message : "",
        };

        if (props.isExternalFile) {
            payload.externalFile = {
                externalId: props.objectId,
                source: props.externalFileSource,
                name: props.externalFileName,
            };
        } else {
            payload.objectId = props.objectId;
        }

        submitShareAsync(payload)
            .then(() => props.onDismiss())
            .catch(() => {
                setSaveError([localize("TeknisktFel")]);
                setIsSharing(false);
            });
    };

    return (
        <Modal
            isOpen={true}
            className="spintr-modal modalWithPopupHeader share-modal"
            onDismiss={props.onDismiss}
            // Let the dropdown's Callout (rendered outside the modal DOM) keep focus.
            focusTrapZoneProps={{ forceFocusInsideTrap: false }}
        >
            <div className="ShareModal">
                {saveError.length > 0 && (
                    <ErrorMessagebar errorList={saveError} onDismiss={() => setSaveError([])} />
                )}
                <PopupHeader text={localize("Dela")} onClose={props.onDismiss} />
                {isSharing ? (
                    <SpintrLoader />
                ) : (
                    <div className="ShareModal-body">
                        <Label className="ShareModal-info" size="body-2" color="mid-grey">
                            {localize(config.infoTextKey)}
                        </Label>
                        <FormControl label={localize("DelaTill")}>
                            <ShareTargetDropdown
                                selectedTarget={selectedTarget}
                                onSelect={setSelectedTarget}
                                currentUser={currentUser}
                            />
                        </FormControl>
                        {showTextarea && (
                            <FormControl label={localize("Meddelande")}>
                                <TextField
                                    value={message}
                                    multiline
                                    onChange={(_event: React.FormEvent, value: string) => setMessage(value || "")}
                                />
                            </FormControl>
                        )}
                        {props.isExternalFile && (
                            <div className="ShareModal-external-file">
                                <ExternalFile
                                    externalId={props.objectId}
                                    source={props.externalFileSource}
                                    name={props.externalFileName}
                                    hideShare={true}
                                />
                            </div>
                        )}
                        <Stack
                            horizontal
                            horizontalAlign="end"
                            tokens={{ childrenGap: 6 }}
                            className="ShareModal-footer"
                        >
                            <DefaultButton text={localize("Avbryt")} onClick={props.onDismiss} />
                            <PrimaryButton
                                text={localize("Dela")}
                                onClick={() => setShowConfirmDialog(true)}
                                disabled={isSharing}
                            />
                        </Stack>
                    </div>
                )}
                <ConfirmDialog
                    show={showConfirmDialog}
                    title={localize("SkapaInnehall")}
                    message={localize("ArDuSakerPaAttDuVillX").replace("{{X}}", localize("DelaInnehall").toLowerCase())}
                    onDismiss={() => setShowConfirmDialog(false)}
                    onConfirm={() => {
                        setShowConfirmDialog(false);
                        onShare();
                    }}
                />
            </div>
        </Modal>
    );
};

export default ShareModal;
